import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Printer } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";

const PHYSICAL = {
  Kunstpark: "Starttafel / Aufsteller. Besucher startet hier den digitalen Weg.",
  "Kleine Brücke": "A5-Schild an der Brücke. Hier wird zwischen zwei Wegvarianten entschieden.",
  Ortskern: "Drei kleine Fundpunkte im Ortskern. Die konkreten Partner und Positionen werden für den Pilot mit der Gemeinde festgelegt.",
  Gedankensäule: "Kleines Schild an der Litfaßsäule. Eine kleine Spur hinterlassen und einen Gedanken bis zum Finale mittragen.",
  "Der Park": "Physisches Fundstück beim markanten abgestorbenen Baum vor der Bühne. Kurz in die Hand nehmen und für den nächsten Menschen zurücklassen.",
  "Der Kasten": "Finale am echten Kunst-Kasten. Als Zielbild wird der geschlossene Kasten gezeigt; vor Ort wird er geöffnet, eine SPUREN-Karte entnommen und der mitgetragene Gedanke kann freiwillig in den Cognitive Space gegeben werden.",
  "Zurück im Kunstpark": "Finale am echten Kunst-Kasten. SPUREN-Karte entnehmen; Gedanke freiwillig in den Cognitive Space.",
};

function Page({ children, className = "" }) {
  return <section className={`print-page mx-auto mb-8 max-w-[210mm] bg-white p-8 shadow-sm ${className}`}>{children}</section>;
}

export default function PitchPrint() {
  const { slug } = useCommunity();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!slug) return;
    let active = true;
    (async () => {
      try {
        const { data: walks } = await api.get(`/communities/${slug}/walks`);
        const spuren = walks.find((w) => w.slug === "spuren") || walks[0];
        if (!spuren) throw new Error("Kein Weg vorhanden.");
        const { data: detail } = await api.get(`/walks/${spuren.id}`);
        if (active) setData(detail);
      } catch {
        if (active) setError("SPUREN konnte nicht geladen werden.");
      }
    })();
    return () => { active = false; };
  }, [slug]);

  const segmentsByFrom = useMemo(() => {
    const map = {};
    for (const segment of data?.segments || []) {
      (map[segment.from_station_id] ||= []).push(segment);
    }
    return map;
  }, [data]);

  if (error) return <div className="p-10 text-center text-sm text-red-700">{error}</div>;
  if (!data) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">Pitchansicht wird geladen …</div>;

  const { walk, stations } = data;

  return (
    <div className="pitch-print bg-[#ece8df] py-6">
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .pitch-print { background: #fff !important; padding: 0 !important; }
          .print-page {
            width: 210mm !important;
            min-height: 297mm !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 16mm !important;
            box-shadow: none !important;
            break-after: page;
            page-break-after: always;
          }
          .print-page:last-child { break-after: auto; page-break-after: auto; }
        }
      `}</style>

      <div className="no-print mx-auto mb-5 flex max-w-[210mm] flex-wrap gap-2 px-2">
        <button className="btn btn-primary" onClick={() => window.print()} data-testid="pitch-print">
          <Printer className="h-4 w-4" /> DRUCKEN / ALS PDF
        </button>
        <a className="btn btn-ghost" href={`/wege/${walk.id}`} target="_blank" rel="noreferrer">
          <ExternalLink className="h-4 w-4" /> LIVE-WEG ÖFFNEN
        </a>
      </div>

      <Page>
        <p className="eyebrow">Pilotvorschlag · St. Ruprecht an der Raab</p>
        <h1 className="mt-4 text-6xl leading-none">{walk.title}</h1>
        <p className="mt-4 max-w-xl text-2xl leading-snug text-[var(--ink-soft)]">{walk.subtitle}</p>
        {walk.hero_image ? (
          <img src={fileUrl(walk.hero_image)} alt="" className="mt-10 h-[118mm] w-full rounded-3xl object-cover" />
        ) : (
          <div className="mt-10 flex h-[118mm] items-center justify-center rounded-3xl border bg-[var(--brand-soft)] text-sm">
            Echtes St.-Ruprecht-Herobild im Admin hochladen
          </div>
        )}
        <div className="mt-8 grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-2xl border p-4"><span className="eyebrow">Dauer</span><p className="mt-1 text-lg">{walk.duration_text}</p></div>
          <div className="rounded-2xl border p-4"><span className="eyebrow">Distanz</span><p className="mt-1 text-lg">{walk.distance_text}</p></div>
          <div className="rounded-2xl border p-4"><span className="eyebrow">Stationen</span><p className="mt-1 text-lg">{stations.length}</p></div>
        </div>
        <p className="mt-8 text-base leading-relaxed">{walk.description}</p>
      </Page>

      <Page>
        <p className="eyebrow">Route · Pilot</p>
        <h2 className="mt-2 text-4xl">Der Weg in einem Blick</h2>
        <div className="mt-10 space-y-0">
          {stations.map((station, index) => (
            <div key={station.id} className="grid grid-cols-[44px_1fr] gap-4">
              <div className="flex flex-col items-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: "var(--brand)" }}>{index + 1}</div>
                {index < stations.length - 1 ? <div className="min-h-14 w-px flex-1 bg-[var(--brand)] opacity-30" /> : null}
              </div>
              <div className="pb-7">
                <p className="text-2xl">{station.title}</p>
                <p className="mt-1 text-sm text-[var(--ink-soft)]">{station.subtitle}</p>
                <p className="mt-2 text-sm leading-relaxed">{station.short_description || station.story_text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border bg-[var(--brand-soft)] p-5 text-sm leading-relaxed">
          <strong>Prinzip:</strong> Das Handy führt, aber der Ort bleibt die Hauptsache. Die Schilder sind bewusst klein.
          Für den Pilot empfehlen wir A5-Hochformat auf kleinen Stehern; Start und Finale dürfen A4 sein.
        </div>
      </Page>

      {stations.map((station, index) => {
        const outgoing = segmentsByFrom[station.id] || [];
        return (
          <Page key={station.id}>
            <div className="flex items-start justify-between gap-5">
              <div>
                <p className="eyebrow">SPUR {String(index + 1).padStart(2, "0")} · {station.subtitle || "Station"}</p>
                <h2 className="mt-2 text-5xl leading-none">{station.title}</h2>
              </div>
              <div className="rounded-full border px-4 py-2 text-xs font-bold">SCHILD {index === 0 || index === stations.length - 1 ? "A4" : "A5"}</div>
            </div>

            {station.hero_image ? (
              <img src={fileUrl(station.hero_image)} alt="" className="mt-8 h-[105mm] w-full rounded-3xl object-cover" />
            ) : (
              <div className="mt-8 flex h-[105mm] items-center justify-center rounded-3xl border bg-[var(--brand-soft)] text-sm">
                Echtes Stationsfoto fehlt noch
              </div>
            )}

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <div>
                <p className="eyebrow">Was Besucher sehen</p>
                <p className="mt-2 text-base leading-relaxed">{station.story_text || station.short_description}</p>
                {station.task_text ? <p className="mt-3 rounded-xl bg-[var(--brand-soft)] p-4 text-sm">{station.task_text}</p> : null}
              </div>
              <div>
                <p className="eyebrow">Was physisch vor Ort passiert</p>
                <p className="mt-2 text-base leading-relaxed">{PHYSICAL[station.title] || "Kleines Stationsschild; die Aufgabe läuft in der App."}</p>
                <p className="mt-4 text-sm text-[var(--ink-soft)]">{station.location_description}</p>
              </div>
            </div>

            {outgoing.length ? (
              <div className="mt-7 border-t pt-5">
                <p className="eyebrow">Danach</p>
                <div className="mt-3 grid gap-3">
                  {outgoing.map((segment) => (
                    <div key={segment.id} className="rounded-2xl border p-4">
                      <p className="font-semibold">{segment.title}</p>
                      <p className="mt-1 text-sm leading-relaxed">{segment.instruction}</p>
                      <p className="mt-2 text-xs text-[var(--ink-soft)]">{segment.distance_text} · {segment.duration_text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Page>
        );
      })}

      <Page>
        <p className="eyebrow">Physischer Abschluss</p>
        <h2 className="mt-2 text-5xl">Die Karte aus dem Kasten</h2>
        <p className="mt-4 max-w-xl text-lg leading-relaxed">
          Am Ende nimmt jede Person genau eine SPUREN-Karte mit. Kein Sketchbook. Der Gedanke vom Rückweg kann freiwillig digital in den Cognitive Space einfließen.
        </p>
        <div className="mt-10 grid grid-cols-2 gap-6">
          <div className="aspect-[2/3] rounded-3xl border bg-[var(--brand-soft)] p-7">
            <p className="eyebrow">Vorderseite</p>
            <p className="mt-8 text-4xl">SPUREN</p>
            <p className="mt-3 text-lg">Du warst hier.<br />Etwas von hier geht mit dir.</p>
          </div>
          <div className="aspect-[2/3] rounded-3xl border p-7">
            <p className="eyebrow">Rückseite</p>
            <p className="mt-8 text-2xl">Was bleibt?</p>
            <p className="mt-4 text-sm leading-relaxed">
              Ein Ort. Ein Satz. Ein Gedanke. Eine Spur, die du vorher vielleicht übersehen hättest.
            </p>
            <p className="mt-10 text-xs text-[var(--ink-soft)]">Konzept: René Ghavami · ART PART</p>
            <p className="mt-2 text-xs">Kleiner QR: „Lern René kennen“</p>
          </div>
        </div>
      </Page>
    </div>
  );
}
